import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  Security,
  ExpandMore,
  CheckCircle,
  Cancel,
  Info,
  Warning,
  Shield,
  LocalShipping,
  Assessment,
  Gavel,
  Euro,
  Assignment
} from '@mui/icons-material';

function MovingInsurance() {
  const [expandedPanel, setExpandedPanel] = useState('panel1');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const insuranceTypes = [
    {
      name: 'Grundhaftung',
      coverage: '620€ pro m³',
      cost: 'Kostenlos (gesetzlich)',
      pros: ['Keine zusätzlichen Kosten', 'Automatisch enthalten'],
      cons: ['Sehr geringe Deckung', 'Oft nicht ausreichend', 'Nur bei grober Fahrlässigkeit']
    },
    {
      name: 'Transportversicherung',
      coverage: 'Bis zum Neuwert',
      cost: '50-200€',
      pros: ['Voller Schadensersatz', 'Alle Schäden abgedeckt', 'Auch bei leichter Fahrlässigkeit'],
      cons: ['Zusätzliche Kosten', 'Selbstbeteiligung möglich']
    },
    {
      name: 'Vollschutz-Paket',
      coverage: 'Rundum-Schutz',
      cost: '100-500€',
      pros: ['Maximaler Schutz', 'Inkl. Montage/Demontage', 'Keine Selbstbeteiligung'],
      cons: ['Höhere Kosten', 'Nicht immer nötig']
    }
  ];

  const coveredDamages = [
    {
      type: 'Transportschäden',
      examples: ['Kratzer und Dellen', 'Glasbruch', 'Wasserschäden', 'Erschütterungsschäden'],
      covered: true
    },
    {
      type: 'Verlust',
      examples: ['Diebstahl während Transport', 'Verlust einzelner Gegenstände', 'Komplettverlust bei Unfall'],
      covered: true
    },
    {
      type: 'Montageschäden',
      examples: ['Schäden beim Ab-/Aufbau', 'Fehlerhafte Montage', 'Beschädigung bei Demontage'],
      covered: true
    },
    {
      type: 'Höhere Gewalt',
      examples: ['Unwetterschäden', 'Unfälle', 'Brand'],
      covered: true
    }
  ];

  const notCovered = [
    'Normale Abnutzung',
    'Bereits bestehende Schäden',
    'Unsachgemäße Verpackung durch Kunden',
    'Elektronikschäden ohne äußere Einwirkung',
    'Schäden durch Haustiere',
    'Bargeld und Wertpapiere'
  ];

  const insuranceTips = [
    {
      title: 'Wann lohnt sich eine Zusatzversicherung?',
      points: [
        'Bei wertvollen Möbeln und Elektronik',
        'Bei Antiquitäten oder Kunstgegenständen',
        'Bei Fernumzügen über 100km',
        'Bei schwierigen Transportbedingungen',
        'Bei hohem Gesamtwert des Umzugsguts'
      ]
    },
    {
      title: 'So schützen Sie sich optimal',
      points: [
        'Inventarliste mit Fotos erstellen',
        'Wertgegenstände dokumentieren',
        'Vorschäden fotografieren',
        'Versicherungsbedingungen genau lesen',
        'Schäden sofort melden'
      ]
    },
    {
      title: 'Im Schadensfall richtig handeln',
      points: [
        'Schäden sofort beim Umzug dokumentieren',
        'Fotos von allen Schäden machen',
        'Schadensprotokoll unterschreiben lassen',
        'Innerhalb von 3 Tagen melden',
        'Alle Belege aufbewahren'
      ]
    }
  ];

  const faqItems = [
    {
      question: 'Was ist der Unterschied zwischen Grundhaftung und Transportversicherung?',
      answer: 'Die Grundhaftung ist gesetzlich vorgeschrieben und deckt nur 620€ pro Kubikmeter ab. Die Transportversicherung bietet eine Absicherung zum Neuwert und deckt auch leichte Fahrlässigkeit ab.'
    },
    {
      question: 'Wann greift meine Hausratversicherung?',
      answer: 'Viele Hausratversicherungen decken Umzugsschäden mit ab. Prüfen Sie Ihre Police oder fragen Sie bei Ihrer Versicherung nach. Oft ist ein Außenversicherungsschutz für 3 Monate enthalten.'
    },
    {
      question: 'Wie hoch sollte die Versicherungssumme sein?',
      answer: 'Die Versicherungssumme sollte dem Wiederbeschaffungswert Ihres gesamten Hausrats entsprechen. Eine Unterversicherung führt im Schadensfall zu reduzierten Leistungen.'
    },
    {
      question: 'Was kostet eine Umzugsversicherung?',
      answer: 'Die Kosten richten sich nach dem Wert des Umzugsguts und der Entfernung. Rechnen Sie mit 0,5-2% des Versicherungswertes als Prämie.'
    },
    {
      question: 'Wie melde ich einen Schaden?',
      answer: 'Dokumentieren Sie Schäden sofort beim Umzug, lassen Sie ein Schadensprotokoll erstellen und melden Sie den Schaden innerhalb von 3 Tagen schriftlich bei der Versicherung.'
    }
  ];

  const comparisonTable = [
    {
      feature: 'Deckungssumme',
      basic: '620€/m³',
      transport: 'Neuwert',
      premium: 'Neuwert + Extras'
    },
    {
      feature: 'Selbstbeteiligung',
      basic: 'Keine',
      transport: '0-500€',
      premium: 'Keine'
    },
    {
      feature: 'Geltungsbereich',
      basic: 'Nur Transport',
      transport: 'Transport + Lagerung',
      premium: 'Komplett-Schutz'
    },
    {
      feature: 'Schäden durch Mitarbeiter',
      basic: '✓ (grobe Fahrlässigkeit)',
      transport: '✓',
      premium: '✓'
    },
    {
      feature: 'Elementarschäden',
      basic: '✗',
      transport: '✓',
      premium: '✓'
    },
    {
      feature: 'Montageschäden',
      basic: '✗',
      transport: 'Optional',
      premium: '✓'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Security sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzugsversicherung
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Rundum geschützt beim Umzug
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Sichern Sie Ihr Hab und Gut optimal ab. Wir erklären Ihnen, welche Versicherungen 
            sinnvoll sind und worauf Sie achten sollten.
          </Typography>
        </Box>

        {/* Important Alert */}
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 6, 
            bgcolor: 'rgba(255,215,0,0.1)', 
            border: '1px solid rgba(255,215,0,0.3)',
            color: '#ffd700'
          }}
          icon={<Warning sx={{ color: '#ffd700' }} />}
        >
          <Typography variant="body1">
            <strong>Wichtig:</strong> Die gesetzliche Grundhaftung von 620€ pro m³ reicht oft nicht aus! 
            Ein normaler Kleiderschrank hat bereits einen höheren Wert.
          </Typography>
        </Alert>

        {/* Insurance Types */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Versicherungsoptionen im Überblick
          </Typography>
          <Grid container spacing={4}>
            {insuranceTypes.map((type, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  bgcolor: '#1a3a44',
                  border: '2px solid',
                  borderColor: index === 1 ? '#55d6dc' : 'rgba(255,255,255,0.08)',
                  height: '100%',
                  position: 'relative'
                }}>
                  {index === 1 && (
                    <Chip
                      label="Empfehlung"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 20,
                        bgcolor: '#55d6dc',
                        color: '#0a1f28',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  <CardContent>
                    <Stack spacing={3}>
                      <Box textAlign="center">
                        <Shield sx={{ fontSize: 50, color: '#55d6dc', mb: 2 }} />
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                          {type.name}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#ffd700' }}>
                          {type.coverage}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#66bb6a', mt: 1 }}>
                          {type.cost}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#66bb6a', mb: 1, fontWeight: 'bold' }}>
                          Vorteile:
                        </Typography>
                        {type.pros.map((pro, idx) => (
                          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <CheckCircle sx={{ fontSize: 16, color: '#66bb6a' }} />
                            <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                              {pro}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#ff6b6b', mb: 1, fontWeight: 'bold' }}>
                          Nachteile:
                        </Typography>
                        {type.cons.map((con, idx) => (
                          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Cancel sx={{ fontSize: 16, color: '#ff6b6b' }} />
                            <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                              {con}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Comparison Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Leistungsvergleich
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1a3a44', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#275664' }}>
                  <TableCell sx={{ color: '#55d6dc', fontWeight: 'bold' }}>Leistung</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Grundhaftung</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Transportversicherung</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Premium-Schutz</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonTable.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell sx={{ color: '#55d6dc' }}>{row.feature}</TableCell>
                    <TableCell align="center" sx={{ color: 'white' }}>{row.basic}</TableCell>
                    <TableCell align="center" sx={{ color: 'white' }}>{row.transport}</TableCell>
                    <TableCell align="center" sx={{ color: 'white' }}>{row.premium}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Covered Damages */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Was ist versichert?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ color: '#66bb6a', mb: 3 }}>
                ✓ Abgedeckte Schäden
              </Typography>
              <Grid container spacing={3}>
                {coveredDamages.map((damage, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper sx={{ 
                      p: 3, 
                      bgcolor: 'rgba(102,187,106,0.1)', 
                      border: '1px solid rgba(102,187,106,0.3)' 
                    }}>
                      <Typography variant="h6" sx={{ color: '#66bb6a', mb: 2, fontWeight: 'bold' }}>
                        {damage.type}
                      </Typography>
                      <List dense>
                        {damage.examples.map((example, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircle sx={{ fontSize: 18, color: '#66bb6a' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={example}
                              sx={{ color: 'white', opacity: 0.9 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ color: '#ff6b6b', mb: 3 }}>
                ✗ Nicht versichert
              </Typography>
              <Paper sx={{ 
                p: 3, 
                bgcolor: 'rgba(255,107,107,0.1)', 
                border: '1px solid rgba(255,107,107,0.3)' 
              }}>
                <List>
                  {notCovered.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Cancel sx={{ color: '#ff6b6b' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        sx={{ color: 'white', opacity: 0.9 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Insurance Tips */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Wichtige Tipps & Hinweise
          </Typography>
          <Grid container spacing={3}>
            {insuranceTips.map((tip, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  bgcolor: '#275664',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%'
                }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      <Info sx={{ color: '#55d6dc', fontSize: 30 }} />
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {tip.title}
                      </Typography>
                    </Stack>
                    <List>
                      {tip.points.map((point, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle sx={{ color: '#55d6dc', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={point}
                            sx={{ color: 'white', opacity: 0.9 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Häufige Fragen
          </Typography>
          {faqItems.map((item, index) => (
            <Accordion
              key={index}
              expanded={expandedPanel === `panel${index + 1}`}
              onChange={handleAccordionChange(`panel${index + 1}`)}
              sx={{
                mb: 2,
                bgcolor: '#1a3a44',
                color: 'white',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#55d6dc' }} />}
                sx={{
                  '&:hover': { bgcolor: '#275664' }
                }}
              >
                <Typography variant="h6">{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ opacity: 0.9 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: 'rgba(85,214,220,0.1)', 
          border: '2px solid #55d6dc',
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Sicher umziehen mit H&N
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Wir beraten Sie gerne zu allen Versicherungsfragen und finden die optimale Absicherung für Ihren Umzug
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Box
              component="button"
              onClick={() => window.location.href = '/contact'}
              style={{
                backgroundColor: '#55d6dc',
                color: '#275664',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
            >
              Beratungstermin vereinbaren
            </Box>
            <Box
              component="button"
              onClick={() => window.location.href = '/moving-checklist'}
              style={{
                backgroundColor: 'transparent',
                color: '#55d6dc',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: '2px solid #55d6dc',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(85,214,220,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Zur Checkliste
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default MovingInsurance;